// @ts-strict-ignore
import capitalize from 'lodash/capitalize';
import uniq from 'lodash/uniq';
import isEqual from 'lodash/isEqual';
import type { ISnackBar } from '@usealloy/component-library';

import { transformSdkConfigForSave } from 'flow/editor/components/MenuBar/MenuBarModals/ThreeDotsModals/SDKConfigModal/sdkHelpers';
import type { SdkConfigPayload } from 'flow/editor/components/MenuBar/MenuBarModals/ThreeDotsModals/SDKConfigModal/types';
import { ajaxFetch } from '~/helpers/api';
import { routes } from '~/helpers/routes';
import BooleanRuleRenderer from '../BooleanRuleRenderer';
import {
  ActionType,
  deleteOptionalUnappliedMlaFilters,
  DiffType,
  generateDiffString,
  generateFlowDiff,
  generateUpdatedActionOptions,
  getActionOptionString,
  getServiceFailureDefaultString,
} from '../flowHelper';
import Logician, { AND_OPERATOR, OR_OPERATOR } from '../Logician';
import type {
  FlowState,
  ReasonCodeGroup,
  RecurringWorkflows,
  Services,
} from '../types';
import { saveMatrixModels } from '../matrix-models/services';
import type { WorkflowTestingSuiteState } from '../TestingSuite/redux/state';
import { getWhatIfFiltersAsQueryParamObj } from '../TestingSuite/pages/TestConfiguration/components/WhatIfConfigurationContent/helpers';


type Badge = {
  color?: string;
  label?: string;
};


/**
 * This whole file comes from the logic within `PreviewChangesPanel`
 * I just added `serviceName, `badge` and changed `headerText` for the `consolidateStrings`
 * and exported it to be used as `PreviewChangeData`
 */
export const getChangeData = ({
  actions,
  actionOptions,
  dataRoutes,
  initialFlowState,
  reasonCodeGroups,
  recurringWorkflows,
  relationships,
  sdkConfig,
  services,
  tags,
  thresholds,
  matrixModels,
  outcomes,
}) => {
  // Early return if the initial state has not been established yet
  if (!initialFlowState) { return []; }
  const diffState = generateFlowDiff(initialFlowState, tags, outcomes, actions,
    actionOptions, thresholds, relationships, dataRoutes, matrixModels, sdkConfig);

  const parseThresholdString = threshold => {
    const types = Object.values(ActionType);
    const thresholdType = types.find(t => threshold?.includes(t));
    const splitString = threshold?.split(`${thresholdType}:`);
    return splitString?.join('');
  };

  const getThresholdsString = array => (
    /** @TODO: [SC - 115701] update AND/OR threshold strings */
    array.map(value => {
      if (value === OR_OPERATOR) return 'or';
      if (value === AND_OPERATOR) return 'and';
      if (Array.isArray(value)) return `(${getThresholdsString(value)})`;

      // Find the correct threshold within initial and current thresholds state since they
      // could have been deleted or created
      const threshold = thresholds.find(t => t.id === +value)
        || initialFlowState.thresholds.find(t => t.id === +value);
      return BooleanRuleRenderer.getThresholdString({
        services,
        reasonCodeGroups,
        tags,
        // Actions will always be current, since only created actions matter
        actions: actions.concat(initialFlowState.actions),
      }, threshold);
    })
      .join(' ')
  );

  /** whatif-reimagined */
  const getLeftServiceName = threshold => {
    if (!threshold) return '';
    const leftAction = actions.find(a => a.id === threshold.leftActionId);
    if (leftAction === undefined || leftAction.type !== 'run') return '';
    return services[leftAction.serviceId].name;
  };

  const consolidateStrings = [];
  /**
   * @TODO: Do not have UX designs for incompatibleChanges yet
   * currently, it's handled by displaying the analysis button or not:
   *
   *  useEffect(() => {
        determines whether to set the runAnalysis button to clickable
        if (incompatibleChanges) {
          triggerAnalysisButton(false);
        } else {
        triggerAnalysisButton(true);
        }
      }, [incompatibleChanges, triggerAnalysisButton]);
   * */
  // let incompatibleChanges = false;
  const unsupportedChangeTypes = ['run', 'input'];

  const getKindText = kind => {
    switch (kind) {
      case DiffType.EDIT:
        return 'Modified';
      case DiffType.NEW:
        return 'Added';
      case DiffType.DELETE:
        return 'Removed';
      default:
        return '';
    }
  };
  /** whatif-reimagined */
  const constructHeaderText = (type, kind) => {
    const kindText = getKindText(kind);
    return `${capitalize(type)} ${kindText}`;
  };


  if ((diffState.actions !== undefined) || (diffState.thresholds !== undefined)) {
    // consolidates the diffs related to action changes
    if (diffState.actions !== undefined) {
      // Filter out cachedChildActionIds property
      diffState.actions = diffState.actions.filter(actionDiff => (
        [
          'cachedChildActionIds',
          'attributes',
          'options',
        ].indexOf(actionDiff.path[1]) === -1
      ));


      if (diffState.actions.length) {
        diffState.actions.forEach(actionDiff => {
          let previousText;
          let currentText;
          let headerText;
          let headerColor;
          let serviceName;
          const badge: Badge = {};
          const isCompatible = true;
          if (actionDiff.kind === DiffType.EDIT && {}.hasOwnProperty.call(actionDiff, 'path')) {
            // Action updated
            const action = actions.find(a => a.id === +actionDiff.path[0]) || {};
            if (action.type !== 'outcome') {
              if (actionDiff.path[1] === 'thresholdsRule') {
                /**
                 * @TODO: [SC-115701]:
                 * need to get serviceName and handle AND/OR threshold changes
                 * this this actionDiff doesn't work with `getLeftServiceName`
                 *  */
                previousText = `${getThresholdsString(new Logician(actionDiff.lhs).toArray())}`;
                currentText = `${getThresholdsString(new Logician(actionDiff.rhs).toArray())}`;
                headerColor = action.tagColor;
                badge.color = action.tagColor;
                badge.label = action.name;
                headerText = constructHeaderText(action.type, actionDiff.kind);
                consolidateStrings.push(
                  {
                    badge,
                    currentText,
                    headerColor,
                    headerText,
                    isCompatible,
                    previousText,
                    serviceName,
                  },
                );
              } else if (actionDiff.path[1] === 'serviceFailureDefault') {
                previousText = `${getServiceFailureDefaultString(action.type, actionDiff.lhs)}`;
                currentText = `${getServiceFailureDefaultString(action.type, actionDiff.rhs)}`;
                consolidateStrings.push(
                  {
                    badge,
                    currentText,
                    headerColor,
                    headerText,
                    isCompatible,
                    previousText,
                    serviceName,
                  },
                );
              } else {
                previousText = `${actionDiff.lhs}`;
                currentText = `${actionDiff.rhs}`;
                headerText = actionDiff.rhs;
                headerColor = action.tagColor;
                badge.color = action.tagColor;
                badge.label = action.name;
                consolidateStrings.push(
                  {
                    badge,
                    currentText,
                    headerColor,
                    headerText,
                    isCompatible,
                    previousText,
                    serviceName,
                  },
                );
              }
            }
          } else if (actionDiff.kind === DiffType.NEW) {
            /** @TODO: [SC - 115697] Update unsupported Service messages */
            if (unsupportedChangeTypes.includes(actionDiff.rhs.type)) {
              consolidateStrings.push({
                isCompatible: false,
                whatHappened: constructHeaderText(actionDiff.rhs.type, actionDiff.kind),
                toWhat: actionDiff.rhs.name,
              });
            }
          }
        });
      }
    }
  }

  // handle parsing actionOptions
  if (diffState.actionOptions !== undefined) {
    diffState.actionOptions = diffState.actionOptions.filter(optionDiff => (
      optionDiff.path[1] !== 'jsonValue'
    ));

    if (diffState.actionOptions.length) {
      diffState.actionOptions.forEach(optionDiff => {
        let previousText;
        let currentText;
        let headerText;
        let headerColor;
        let serviceName;
        const badge: Badge = {};
        const isCompatible = true;
        if (optionDiff.kind === DiffType.EDIT && {}.hasOwnProperty.call(optionDiff, 'path')) {
          const actionOption = actionOptions.find(ao => ao.id === +optionDiff.path[0]);
          const action = actions.find(a => a.id === actionOption.actionId);

          // Early return for illegible changes
          if (action && unsupportedChangeTypes.includes(action.type)) {
            consolidateStrings.push({
              isCompatible: false,
              whatHappened: constructHeaderText(action.type, optionDiff.kind),
              toWhat: action.name,
            });
            return;
          }

          previousText = `${getActionOptionString(actionOption.key, optionDiff.lhs, action.name, recurringWorkflows)}`;
          currentText = `${getActionOptionString(actionOption.key, optionDiff.rhs, action.name, recurringWorkflows)}`;
          headerText = constructHeaderText(action.type, optionDiff.kind);
          headerColor = action.tagColor;
          badge.color = action.tagColor;
          serviceName = getLeftServiceName(optionDiff);
          consolidateStrings.push(
            {
              badge,
              currentText,
              headerColor,
              headerText,
              isCompatible,
              previousText,
              serviceName,
            },
          );
        } else if (optionDiff.kind === DiffType.NEW) {
          const action = actions.find(a => a.id === optionDiff.rhs.actionId);
          if (action) {
            currentText = `'${optionDiff.rhs.key} with value '${getActionOptionString(optionDiff.rhs.key, optionDiff.rhs.value, action.name, recurringWorkflows)}'`;
          } else {
            currentText = optionDiff.rhs.key;
          }
          if (action && unsupportedChangeTypes.includes(action.type)) {
            // incompatibleChanges = true;
          } else {
            if (action && action.type !== ActionType.TAG) {
              headerText = `${capitalize(action.type)}:${action.name}`;
              headerColor = action.tagColor;
              badge.color = action.tagColor;
              badge.label = action.name;
            } else if (action) {
              headerText = action.name;
            } else {
              headerText = optionDiff.rhs.key;
              headerColor = null;
            }

            consolidateStrings.push(
              {
                currentText,
                headerColor,
                headerText,
                isCompatible,
              },
            );
          }
        } else if (optionDiff.kind === DiffType.DELETE) {
          const action = initialFlowState.actions
            .find(a => a.id === optionDiff.lhs.actionId);
          previousText = `'${optionDiff.lhs.key}' with value '${getActionOptionString(optionDiff.lhs.key, optionDiff.lhs.value, action.name, recurringWorkflows)}'`;
          currentText = 'Deleted from workflow';
          headerText = action.name;
          headerColor = action.tagColor;
          badge.color = action.tagColor;
          badge.label = action.name;
          consolidateStrings.push(
            {
              badge,
              currentText,
              headerColor,
              headerText,
              isCompatible,
              previousText,
              serviceName,
            },
          );
        }
      });
    }
  }

  // Handle parsing thresholds
  if (diffState.thresholds !== undefined) {
    const thresholdProps = {
      actions: actions.concat(initialFlowState.actions),
      services,
      reasonCodeGroups,
      tags,
    };
    const diffStateThresholdIds = [];
    diffState.thresholds.forEach(thresholdDiff => {
      let previousText;
      let currentText;
      let headerText;
      let headerColor;
      let serviceName;
      const badge: Badge = {};
      // edits to thresholds
      if (thresholdDiff.kind === DiffType.EDIT && {}.hasOwnProperty.call(thresholdDiff, 'path')) {
        const newThreshold = thresholds.find(t => t.id === +thresholdDiff.path[0]);
        if (newThreshold && diffStateThresholdIds.indexOf(newThreshold.id) === -1) {
          const newThresholdString = BooleanRuleRenderer.getThresholdString(
            thresholdProps,
            newThreshold,
          );
          const action = actions.find(a => a.id === newThreshold.actionId);
          const oldThreshold = initialFlowState.thresholds.find(t => (
            t.id === newThreshold.id));
          // This is a new threshold that deep-diff didn't catch.
          if (oldThreshold !== undefined) {
            const oldThresholdString = BooleanRuleRenderer.getThresholdString(
              thresholdProps, oldThreshold,
            );

            const isChangeInMLA = newThreshold?.leftAttrComponents?.some(attr => {
              const oldAttr = oldThreshold.leftAttrComponents
                .find(a => a.attribute_component_id === attr.attribute_component_id);
              return !oldAttr || !isEqual(attr?.value, oldAttr?.value);
            });

            if (isChangeInMLA) {
              consolidateStrings.push({
                isCompatible: false,
                whatHappened: constructHeaderText(action.type, thresholdDiff.kind),
                toWhat: action.name,
              });
            }

            // If strings are not the same, the threshold actually changed -_-
            if (newThresholdString !== oldThresholdString) {
              previousText = parseThresholdString(oldThresholdString);
              currentText = parseThresholdString(newThresholdString);
              headerText = `${capitalize(action.type)} Threshold ${getKindText(thresholdDiff.kind)}`;
              headerColor = action.tagColor;
              badge.color = action.tagColor;
              badge.label = action.name;
              serviceName = getLeftServiceName(newThreshold);
              consolidateStrings.push(
                {
                  badge,
                  currentText,
                  headerColor,
                  headerText,
                  isCompatible: true,
                  previousText,
                  serviceName,
                },
              );
            }
          }

          diffStateThresholdIds.push(newThreshold.id);
        }
        // additions to thresholds
      } else if (thresholdDiff.kind === DiffType.NEW) {
        const threshold = thresholds.find(t => t.id === +thresholdDiff.path[0]);
        const thresholdString = BooleanRuleRenderer.getThresholdString(
          thresholdProps,
          threshold,
        );
        // actionName: serviceName: thresholdDetails
        const action = actions.find(a => a.id === thresholdDiff.rhs.actionId);
        currentText = parseThresholdString(thresholdString);
        serviceName = getLeftServiceName(thresholdDiff.rhs);
        const isCompatible = thresholdDiff.rhs?.leftAttrComponents?.length <= 1
          && !unsupportedChangeTypes.includes(action.type);

        if (action !== undefined) {
          headerColor = action.tagColor;
          badge.color = action.tagColor;
          badge.label = action.name;
          // incompatible types to not display
          if (!isCompatible) {
            const sourceAction = actions.find(a => a.id === thresholdDiff.rhs.leftActionId);
            consolidateStrings.push({
              isCompatible: false,
              whatHappened: `${sourceAction.name}: Multi Level Attribute Added:`,
              // The optional chaining is just in case something's undefined.
              // Even though showing "undefined" isn't great, it's better than
              // the app completely failing for this context.
              toWhat: services[sourceAction.serviceId]
                ?.attributes[thresholdDiff.rhs.leftAttrId]
                ?.display_name,
            });
          } else {
            headerText = constructHeaderText(action.type, thresholdDiff.kind);

            consolidateStrings.push(
              {
                badge,
                currentText,
                headerColor,
                headerText,
                isCompatible,
                serviceName,
              },
            );
          }
        }

        // deletions from thresholds
      } else if (
        thresholdDiff.kind === DiffType.DELETE
        // Exclude threshold MLA components from this diff check since that change will be
        // captured in the generated threshold string
        && ['leftAttrComponents', 'rightAttrComponents']
          .every(key => !thresholdDiff.path.includes(key))
      ) {
        const deleted = typeof thresholdDiff.lhs === 'object'
          ? thresholdDiff.lhs
          : initialFlowState.thresholds.find(t => t.actId === thresholdDiff.lhs);
        const thresholdString = BooleanRuleRenderer.getThresholdString(
          thresholdProps,
          deleted,
        );
        // actionName: serviceName: thresholdDetails
        currentText = 'Deleted from workflow';
        previousText = parseThresholdString(thresholdString);
        serviceName = getLeftServiceName(deleted);

        const action = actions.find(a => a.id === deleted.actionId)
          || initialFlowState.actions.find(a => a.id === deleted.actionId);
        if (action !== undefined) {
          headerColor = action.tagColor;
          badge.color = action.tagColor;
          badge.label = action.name;
          if (action.type === ActionType.OUTCOME) {
            headerText = `Outcome:${action.name}`;
          } else {
            headerText = action.name;
          }
        }
        consolidateStrings.push(
          {
            badge,
            currentText,
            headerColor,
            headerText,
            isCompatible: true,
            previousText,
            serviceName,
          },
        );
      }
    });
  }
  // handle parsing matrix models
  /** @TODO: Needs final design, not yet edited */
  const { matrixModels: matrixModelsDiff } = diffState;
  if (matrixModelsDiff && matrixModelsDiff.length) {
    const matrixModelIds = uniq(matrixModelsDiff.map(model => +model.path[0]));
    matrixModels.filter(m => matrixModelIds.includes(m.matrix_model_id)).forEach(({ name }) => {
      const currentText = 'Changes made to values';
      const headerText = `Matrix Model: ${name}`;
      consolidateStrings.push({ headerText, currentText, isCompatible: true });
    });
  }

  return consolidateStrings;
};

/**
 * State object to close any other visible UI element on the workflow builder page.
 * For example: the panel that shows the selected/hovered action.
 */
export const getPreTestingSuiteStateChanges = (
  state: { attributesDisplayOpen: boolean; selectedAction: boolean; hoverActions: any[] },
) => {
  const changes: Partial<typeof state> = {};
  if (state.attributesDisplayOpen) { changes.attributesDisplayOpen = false; }
  if (state.selectedAction) { changes.selectedAction = false; }
  if (state.hoverActions.length) { changes.hoverActions = []; }
  return changes;
};


type IsActionUpdateRelevantForWhatIf = {
  thresholdsRule: string;
};

export const isActionUpdateRelevantForWhatIf = ({
  thresholdsRule,
}: IsActionUpdateRelevantForWhatIf) => {
  const andOrORCharactersPattern = /[&|]/;
  return andOrORCharactersPattern.test(thresholdsRule ?? '');
};

export interface MakeFetchWithWorkflowInfoParams {
  deps: {
    applicationToken: string;
    reasonCodeGroups: ReasonCodeGroup[];
    recurringWorkflows: RecurringWorkflows;
    services: Services;
    typhonDomain: string;
  };
  /** can be either create version or the what-if url */
  url: string;
  /** flowState refers to the state of the Workflow editor */
  initialFlowState: FlowState;
  updatedFlowState?: FlowState;
  extraPostData: { [k: string]: any };
}

/**
  * This method is very similar to Flow.jsx's handleSaveApplicationVersion.
  * This is so a new version can be created in the Testing Suite w/o the side
  * effects of handleSaveApplicationVersion.
  * It's also for triggering a what-if analysis. Basically, anything what-if
  * network request related that needs workflow info.
  * Duplication is easier to fix than working with the wrong abstraction.
  */
export const makeFetchWithWorkflowInfo = async ({
  deps,
  url,
  initialFlowState,
  updatedFlowState,
  extraPostData,
}: MakeFetchWithWorkflowInfoParams) => {
  // versionChanges is usually generated on save instead of as things are
  // changed. We can't call `setState({ versionChanges ... })` because that
  // triggers a modal to appear.
  const versionChanges = generateDiffString(
    initialFlowState,
    updatedFlowState?.tags || [],
    updatedFlowState?.outcomes || [],
    updatedFlowState?.actions || [],
    updatedFlowState?.actionOptions || [],
    updatedFlowState?.thresholds || [],
    updatedFlowState?.relationships || [],
    updatedFlowState?.dataRoutes || [],
    // TODO: Find better default
    updatedFlowState?.sdkConfig || initialFlowState.sdkConfig,
    deps,
  );

  const {
    processedThresholds,
    processedDataRoutes,
  } = deleteOptionalUnappliedMlaFilters({
    thresholds: updatedFlowState?.thresholds || [],
    dataRoutes: updatedFlowState?.dataRoutes || [],
  });

  const {
    // this would be an empty array if there are no changes/new matrices
    matrixModelActionOptions,
    updatedMatrixModelActionOptions,
  } = await saveMatrixModels(deps.typhonDomain, updatedFlowState?.matrixModels || []);

  const sdkConfig = updatedFlowState?.sdkConfig?.fields
    ? transformSdkConfigForSave(updatedFlowState.sdkConfig) : updatedFlowState.sdkConfig;

  return ajaxFetch(url, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({
      actions: updatedFlowState?.actions,
      action_options: generateUpdatedActionOptions(
        updatedFlowState?.actionOptions,
        // Are these the same shapes as matrixModels?
        matrixModelActionOptions,
        updatedMatrixModelActionOptions),
      thresholds: processedThresholds,
      data_routes: processedDataRoutes,
      relationships: updatedFlowState?.relationships,
      version_notes: 'This version was created by What-if Analysis.', // Not req'd
      version_changes: versionChanges,
      application_token: deps.applicationToken, // not req'd
      sdk_config: sdkConfig,
      ...extraPostData,
    }),
  });
};

/**
 * Create an object representing the workflow changes needed for what-if analysis.
 */
export const getWorkflowDataForWhatIf = async (
  deps: {
    reasonCodeGroups: ReasonCodeGroup[];
    recurringWorkflows: RecurringWorkflows;
    services: Services;
    typhonDomain: string;
  },
  initialFlowState: FlowState,
  updatedFlowState: FlowState,
) => {
  // versionChanges is usually generated on save instead of as things are
  // changed. We can't call `setState({ versionChanges ... })` because that
  // triggers a modal to appear.
  const versionChanges = generateDiffString(
    initialFlowState,
    updatedFlowState?.tags || [],
    updatedFlowState?.outcomes || [],
    updatedFlowState?.actions || [],
    updatedFlowState?.actionOptions || [],
    updatedFlowState?.thresholds || [],
    updatedFlowState?.relationships || [],
    updatedFlowState?.dataRoutes || [],
    updatedFlowState?.sdkConfig || initialFlowState.sdkConfig,
    deps,
  );

  const {
    processedThresholds,
    processedDataRoutes,
  } = deleteOptionalUnappliedMlaFilters({
    thresholds: updatedFlowState?.thresholds || [],
    dataRoutes: updatedFlowState?.dataRoutes || [],
  });

  const {
    // this would be an empty array if there are no changes/new matrices
    matrixModelActionOptions,
    updatedMatrixModelActionOptions,
  } = await saveMatrixModels(deps.typhonDomain, updatedFlowState?.matrixModels || []);

  const sdkConfig = updatedFlowState?.sdkConfig?.fields
    ? transformSdkConfigForSave(updatedFlowState.sdkConfig) : updatedFlowState.sdkConfig;

  return {
    actions: updatedFlowState?.actions,
    action_options: generateUpdatedActionOptions(
      updatedFlowState?.actionOptions,
      // Are these the same shapes as matrixModels?
      matrixModelActionOptions,
      updatedMatrixModelActionOptions),
    thresholds: processedThresholds,
    data_routes: processedDataRoutes,
    // Using updatedFlowState since initialFlowState doesn't have version info
    parent_version: updatedFlowState.version.public_version,
    relationships: updatedFlowState?.relationships,
    version_notes: 'This version was created by What-if Analysis.',
    version_changes: versionChanges,
    sdk_config: sdkConfig,
  };
};


/**
 * These are the properties needed when performing a what-if analysis using
 * changes to a workflow. These params are used to create a hidden version.
 */
export interface WhatIfWorkflowData {
  actions: FlowState['actions'];
  action_options: FlowState['actionOptions'];
  thresholds: FlowState['thresholds'];
  data_routes: FlowState['dataRoutes'];
  parent_version: number;
  relationships: FlowState['relationships'];
  sdk_config?: SdkConfigPayload;
  version_changes?: string;
  version_notes?: string;
  failure_handling_mode: string;
}

/**
 * application_version_old is the version to source evaluations from.
 * filters are What-If's filters but with evaluationTokens as an optional string
 * array instead of an optional string. The string, if it exists, is split by
 * commas to form a list of tokens.
 */
export interface WhatIfCommonParams {
  application_version_old: number;
  filters: ReturnType<typeof getWhatIfFiltersAsQueryParamObj>;
}

/**
 * The args for when you want to run what-if against two separate versions.
 * (e.g. Testing V2's workflow against V1's data')
 */
export interface WhatIfParamsToCompareVersions extends WhatIfCommonParams {
  application_version_new: number;
}

/**
 * The args for when you want to test changes to a workflow. The changes are
 * not saved to a new workflow version.
 */
export interface WhatIfParamsToTestChanges extends WhatIfCommonParams, WhatIfWorkflowData { }

/**
 * A union of the possible What-If Payloads
 */
export type WhatIfPayload = WhatIfParamsToCompareVersions | WhatIfParamsToTestChanges;

export interface FormatReqParamsArgs {
  areThereChanges: boolean;
  versionToTest: number;
  versionToTestAgainst: number;
  willCreateNewEvals: boolean;
  workflowToken: string;
  filters?: WorkflowTestingSuiteState['whatIf']['filters'];
  workflowData?: WhatIfWorkflowData;
}

/**
 * Creates the data needed to perform what-if based off of whether two versions
 * or unsaved changes are being compared.
 */
export const formatReqParams = ({
  areThereChanges,
  versionToTest,
  versionToTestAgainst,
  willCreateNewEvals,
  workflowToken,
  filters,
  workflowData,
}: FormatReqParamsArgs): WhatIfPayload => {
  const filtersForPostReq = filters
    ? getWhatIfFiltersAsQueryParamObj({
      filters,
      workflowToken,
      versionToTestAgainst,
    })
    : {};
  const commonData = {
    // application_version_old/versionToTestAgainst is the version evals are sourced from
    application_version_old: versionToTestAgainst,
    filters: filtersForPostReq,
  };

  const isTestForUnsavedChangesAgainstAnotherVersion = (
    areThereChanges && !willCreateNewEvals
  );

  // Check if we're testing changes to a version instead of two existing versions
  if (
    versionToTest === versionToTestAgainst
    || isTestForUnsavedChangesAgainstAnotherVersion
  ) {
    return {
      ...commonData,
      ...workflowData,
    };
  }

  return {
    ...commonData,
    application_version_new: versionToTest,
  };
};

/**
 * Dependencies needed that we can't just import (because they depend on context
 * or redux or other). This is data that's not expected to change as a user
 * interacts with the UI.
 */
export interface WhatIfDependencies {
  applicationToken: string;
  addSnackBar: (snackBar: ISnackBar) => void;
}

/**
 * The arguments that can change based on interactions with the UI.
 */
export interface WhatIfArgs {
  areThereChanges: boolean;
  versionToTest: number;
  versionToTestAgainst: number;
  willCreateNewEvals: boolean;
  filters?: WorkflowTestingSuiteState['whatIf']['filters'];
  queryParams?: string;
  workflowData?: WhatIfWorkflowData;
}

/**
 * What is needed to perform a What-If analysis.
 * There are two ways to perform what-if:
 * - sending two version numbers to compare. (e.g. V2's workflow against V1's evals')
 * - sending the version number of the version you want to source evals from &
 *   the changed workflow data. (e.g. Using V1's evals to test against unsaved changes)
 */
export const performWhatIfAnalysis = async (
  {
    applicationToken,
    addSnackBar,
  }: WhatIfDependencies,
  {
    areThereChanges,
    versionToTest,
    versionToTestAgainst,
    willCreateNewEvals,
    filters,
    queryParams = '',
    workflowData,
  }: WhatIfArgs,
) => {
  const urlPath = routes.applications.whatIfAnalysis(applicationToken);
  const url = `${urlPath}${queryParams}`;
  const postData = formatReqParams({
    workflowToken: applicationToken,
    areThereChanges,
    versionToTest,
    versionToTestAgainst,
    willCreateNewEvals,
    filters,
    workflowData,
  });

  try {
    const data = await ajaxFetch(url, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(postData),
    });

    if (data.result !== 'success') {
      throw new Error(data.response?.error?.message
        || 'The network request was not deemed a success');
    }

    return data;
  } catch (err) {
    addSnackBar({
      title: err.message || 'Something went wrong. Please try again or contact support.',
      type: 'danger',
    });
    throw err;
  }
};
